import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/Skeleton";
import { toast } from "sonner";
import {
  Sparkles, ArrowRight, User, FileText, LayoutGrid, Presentation,
  MessageSquare, Globe, Store, TrendingUp, Award, Eye, Briefcase,
  Library, GraduationCap,
} from "lucide-react";

const ICONS = {
  user: User, fileText: FileText, layoutGrid: LayoutGrid,
  presentation: Presentation, messages: MessageSquare,
  globe: Globe, store: Store,
};

const QUICK_ACTIONS = [
  { id: "improve-profile",   label: "Improve My Profile",         icon: User,         to: "/profile",                          desc: "Hero + headline + bio + categories" },
  { id: "ai-branding",       label: "AI Personal Branding",       icon: Sparkles,     to: "/growth-studio/branding",          desc: "10 AI text generators" },
  { id: "premium-cv",        label: "Build Premium CV",           icon: FileText,     to: "/resume",                          desc: "AI resume + portfolio templates" },
  { id: "cv-improve",        label: "Improve Existing CV",        icon: GraduationCap,to: "/growth-studio/cv-improve",        desc: "Reshape for target context" },
  { id: "portfolio",         label: "Teaching Portfolio Builder", icon: LayoutGrid,   to: "/growth-studio/portfolio",         desc: "Showcase your best work" },
  { id: "demo-lesson",       label: "Prepare Demo Lesson",        icon: Presentation, to: "/growth-studio/demo-lesson",       desc: "5 / 10 / 20-min scripts" },
  { id: "interview-prep",    label: "Interview Prep",             icon: MessageSquare,to: "/growth-studio/interview-prep",    desc: "Anticipated Q&A · STAR" },
  { id: "global-readiness",  label: "Global Opportunity Readiness", icon: Globe,      to: "/growth-studio/global-readiness",   desc: "Unlock international roles" },
  { id: "publish-portfolio", label: "Publish Portfolio",          icon: Store,        to: "/growth-studio/portfolio",         desc: "Public shareable URL" },
];

export default function GrowthStudio() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/growth/dashboard");
        if (alive) setData(data);
      } catch {
        if (alive) toast.error("Could not load Growth Studio dashboard");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <Layout title="Growth Studio">
      {/* Hero */}
      <div className="mb-6">
        <span className="tb-chip-lavender inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
          <TrendingUp className="h-3 w-3" /> Educator Growth Studio
        </span>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight" data-testid="growth-title">
          Build your profile. Grow your reputation. Get discovered globally.
        </h1>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
          Your premium career and identity hub — AI personal branding, portfolio builder, demo lesson prep,
          interview practice, and global opportunity readiness. Everything you need to stand out as an educator.
        </p>
      </div>

      {/* Score Hero */}
      {loading ? (
        <Skeleton className="h-48 mb-6" />
      ) : data && (
        <div className="tb-card-feature relative overflow-hidden mb-6 p-5 sm:p-7 grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="growth-scores">
          <div className="absolute inset-0 tb-grid-bg opacity-50 pointer-events-none" />
          <div className="relative lg:col-span-3 flex items-center gap-4">
            <ScoreRing value={data.overall} accent="amber" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">Overall</p>
              <p className="font-display text-2xl tracking-tight font-medium">{levelFromScore(data.overall)}</p>
              <p className="text-xs text-muted-foreground">{data.overall} / 100</p>
            </div>
          </div>

          <div className="relative lg:col-span-9 grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
            <ScoreMini label="Profile strength"        value={data.profile_strength}      testid="score-profile" />
            <ScoreMini label="Hiring readiness"        value={data.hiring_readiness}      testid="score-hiring" />
            <ScoreMini label="Resume quality"          value={data.resume_quality}        testid="score-resume" />
            <ScoreMini label="Portfolio quality"       value={data.portfolio_quality}     testid="score-portfolio" />
            <ScoreMini label="Marketplace visibility" value={data.marketplace_visibility} testid="score-marketplace" />
            <ScoreMini label="Global readiness"        value={data.global_readiness}      testid="score-global" />
          </div>
        </div>
      )}

      {/* Counters */}
      {!loading && data && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="growth-counters">
          <KpiCard icon={Eye}         label="Profile views"   value={data.counters.public_profile_views} testid="growth-kpi-profile-views" />
          <KpiCard icon={FileText}    label="Resumes"         value={data.counters.resumes}              testid="growth-kpi-resumes" />
          <KpiCard icon={LayoutGrid}  label="Portfolio items" value={data.counters.portfolio_items}      testid="growth-kpi-portfolio" />
          <KpiCard icon={Library}     label="Published lessons" value={data.counters.published_lessons}   testid="growth-kpi-lessons" />
          <KpiCard icon={Briefcase}   label="Applications"    value={data.counters.applications}         testid="growth-kpi-applications" />
          <KpiCard icon={Eye}         label="Resume views"    value={data.counters.resume_views}         testid="growth-kpi-resume-views" />
        </div>
      )}

      {/* Quick actions */}
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground mb-3 inline-flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" /> Quick actions
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="growth-quick-actions">
          {QUICK_ACTIONS.map((q) => {
            const Icon = q.icon;
            return (
              <button
                key={q.id}
                onClick={() => navigate(q.to)}
                className="text-left group tb-card p-4 hover:border-primary/40 hover:shadow-md transition relative overflow-hidden"
                data-testid={`growth-action-${q.id}`}
              >
                <div className="flex items-start gap-3">
                  <span className="h-10 w-10 rounded-xl tb-chip-lavender grid place-items-center shrink-0">
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium leading-snug">{q.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{q.desc}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground group-hover:translate-x-1 group-hover:text-primary transition" />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Next Actions */}
      {!loading && data && data.next_actions?.length > 0 && (
        <div className="tb-card p-5" data-testid="growth-next-actions">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground inline-flex items-center gap-1.5 mb-3">
            <Award className="h-3 w-3" /> Suggested next steps
          </p>
          <ul className="space-y-2">
            {data.next_actions.map((a) => {
              const Icon = ICONS[a.icon] || Sparkles;
              return (
                <li key={a.id}>
                  <button
                    onClick={() => navigate(a.path)}
                    className="w-full flex items-center gap-3 rounded-xl p-3 hover:bg-muted/60 transition text-left"
                    data-testid={`growth-next-${a.id}`}
                  >
                    <span className="h-7 w-7 rounded-lg tb-chip-amber grid place-items-center shrink-0">
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <span className="flex-1 text-sm font-medium">{a.label}</span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Layout>
  );
}

/* ----- Helpers ----- */

function ScoreMini({ label, value, testid }) {
  const v = Math.max(0, Math.min(100, value || 0));
  return (
    <div className="rounded-xl border border-border bg-card/60 p-3" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-xl tracking-tight font-medium">{v}<span className="text-xs text-muted-foreground">/100</span></p>
      <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
        <span className="block h-full rounded-full bg-gradient-to-r from-violet-500 to-amber-400" style={{ width: `${v}%`, transition: "width 0.5s ease-out" }} />
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-xl border border-border bg-card/60 p-3" data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className="h-7 w-7 rounded-lg tb-chip-sky grid place-items-center"><Icon className="h-3.5 w-3.5" /></span>
        <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">{label}</p>
      </div>
      <p className="mt-1.5 font-display text-2xl tracking-tight font-medium">{value ?? 0}</p>
    </div>
  );
}

function ScoreRing({ value }) {
  const r = 38;
  const c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(100, value || 0));
  const offset = c - (v / 100) * c;
  return (
    <div className="relative" data-testid="growth-score-ring">
      <svg width="96" height="96" viewBox="0 0 96 96" className="-rotate-90">
        <circle cx="48" cy="48" r={r} fill="none" stroke="hsl(var(--border))" strokeWidth="8" />
        <circle
          cx="48" cy="48" r={r} fill="none"
          stroke="url(#growth-grad)" strokeWidth="8" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.6s ease-out" }}
        />
        <defs>
          <linearGradient id="growth-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#a78bfa" />
            <stop offset="100%" stopColor="#fbbf24" />
          </linearGradient>
        </defs>
      </svg>
      <span className="absolute inset-0 grid place-items-center font-display text-2xl font-medium tracking-tight">{v}</span>
    </div>
  );
}

function levelFromScore(s) {
  if (s >= 85) return "Outstanding";
  if (s >= 70) return "Strong";
  if (s >= 50) return "Solid";
  if (s >= 25) return "Getting started";
  return "Brand new";
}
